import {
  Button,
  List,
  Notification,
  Table,
  type TableProps,
  Typography,
  Viewport,
} from '@omnipresentgroup/design-system';
import { SortOrder, useInvoiceReports } from '@omnipresentgroup/domains';
import {
  hasFinanceScope,
  hasSuperManagerScope,
} from '@omnipresentgroup/helpers';
import { useFlags } from '@omnipresentgroup/launchdarkly';
import type { InvoiceReport } from '@omnipresentgroup/types';
import {
  getCoreRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { INVOICE_DETAILS_PATH } from '../../constants';
import { useManagerUser } from '../../hooks';
import { WrappedMixpanel } from '../../mixpanelWrapper';
import { InvoiceCardList } from './components';
import { useInvoiceReportsTableConfig } from './hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const EMPTY_STATE_TITLE = 'No invoices available';
const EMPTY_STATE_DESCRIPTION = `You haven't received any bills so far`;

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 0;

export const INVOICE_REPORTS_LIST_PAGE_TITLE = 'Invoices';

const SORT_URL_PARAM = 'sort';
const ORDER_URL_PARAM = 'order';
const PAGE_URL_PARAM = 'page';
const SIZE_URL_PARAM = 'size';
const DEFAULT_SORT_COLUMN: keyof InvoiceReport = 'issueDate';

const EMPTY_ARRAY: InvoiceReport[] = [];
const EMPTY_LOADING_ARRAY = Array.from(
  { length: 10 },
  () => ({}) as InvoiceReport,
);

const INVOICE_REPORTS_INFO = [
  'Payments may take up to 72 hours to clear, and a further 24 hours to display here.',
];

export const InvoiceReportList = () => {
  const navigate = useNavigate();

  const { enableInvoiceBreakdown } = useFlags();

  const { companyId, authUser, userId } = useManagerUser();

  const hasPermissionToGetInvoiceReports = useMemo(
    () =>
      (authUser && hasSuperManagerScope(authUser)) ||
      (authUser && hasFinanceScope(authUser)),
    [authUser],
  );

  const [searchParameters, setSearchParameters] = useSearchParams();

  const urlParameters = useMemo(
    () => ({
      page: searchParameters.get(PAGE_URL_PARAM),
      size: searchParameters.get(SIZE_URL_PARAM),
      sort: searchParameters.get(SORT_URL_PARAM),
      order: searchParameters.get(ORDER_URL_PARAM),
    }),
    [searchParameters],
  );

  const getSortFromTableSorting = (sorting: SortingState) => sorting[0].id;

  const getOrderFromTableSorting = (sorting: SortingState): SortOrder =>
    sorting[0].desc ? SortOrder.DESC : SortOrder.ASC;

  const [tableSorting, setTableSorting] = useState<SortingState>([
    {
      id: urlParameters.sort || DEFAULT_SORT_COLUMN,
      desc:
        urlParameters.order === null || urlParameters.order === SortOrder.DESC,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: Number(urlParameters.page) || DEFAULT_PAGE_INDEX,
    pageSize: Number(urlParameters.size) || DEFAULT_PAGE_SIZE,
  });

  const {
    data: invoiceReportResult,
    isLoading,
    isError,
    isFetchedAfterMount,
  } = useInvoiceReports({
    page: pageIndex + 1,
    size: pageSize,
    companyId: companyId ?? '',
    enabled: hasPermissionToGetInvoiceReports && !!companyId,
    sort: getSortFromTableSorting(tableSorting),
    order: getOrderFromTableSorting(tableSorting),
  });

  useEffect(() => {
    setSearchParameters({
      [PAGE_URL_PARAM]: String(pageIndex),
      [SIZE_URL_PARAM]: String(pageSize),
      [SORT_URL_PARAM]: getSortFromTableSorting(tableSorting),
      [ORDER_URL_PARAM]: getOrderFromTableSorting(tableSorting),
    });
  }, [tableSorting, pageIndex, pageSize, setSearchParameters]);

  const { columns } = useInvoiceReportsTableConfig();

  const totalNumberOfPages = useMemo(
    () =>
      invoiceReportResult?.count
        ? Math.ceil(Math.max(invoiceReportResult.count / pageSize, 1))
        : DEFAULT_PAGE_INDEX,
    [invoiceReportResult?.count, pageSize],
  );

  const paginationProps: TableProps['paginationProps'] = useMemo(() => {
    return {
      pageSize,
      pageIndex: pageIndex,
      canNextPage: pageIndex + 1 < totalNumberOfPages,
      canPreviousPage: pageIndex > DEFAULT_PAGE_INDEX,
      pageCount: totalNumberOfPages,
      totalCount: isLoading ? pageSize : invoiceReportResult?.count,
    };
  }, [
    pageSize,
    pageIndex,
    totalNumberOfPages,
    isLoading,
    invoiceReportResult?.count,
  ]);

  const tableOptions: Omit<TableOptions<InvoiceReport>, 'data' | 'columns'> = {
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    enableSortingRemoval: false,
    sortDescFirst: true,
    state: {
      sorting: tableSorting,
    },
    onSortingChange: setTableSorting,
    manualSorting: true,
  };

  const table = useReactTable({
    data: isLoading
      ? EMPTY_LOADING_ARRAY
      : (invoiceReportResult?.data ?? EMPTY_ARRAY),
    columns: columns,
    ...tableOptions,
  });

  return (
    <>
      <Notification
        id="invoice-reports-list-notification"
        title="Important"
        description={
          <List
            showDisc
            gap="0"
            items={INVOICE_REPORTS_INFO.map((warning, index) => ({
              id: `invoice-reports-info-${index}`,
              name: warning,
            }))}
            keyExtractor="id"
            render={(item) => (
              <Typography as="span" size="14">
                {item.name}
              </Typography>
            )}
          />
        }
      />
      <Viewport devices={['laptop', 'desktop', 'highRes']}>
        <Table
          id="invoice-reports-list"
          table={table}
          loading={isLoading && !isFetchedAfterMount}
          errorStateProps={{
            id: 'invoice-reports-list-error',
            title: ERROR_STATE_TITLE,
            description: ERROR_STATE_DESCRIPTION,
            callToAction: (
              <Button onClick={window.location.reload}>Retry</Button>
            ),
          }}
          emptyStateProps={{
            id: 'invoice-reports-list-empty-state',
            title: EMPTY_STATE_TITLE,
            description: EMPTY_STATE_DESCRIPTION,
          }}
          showErrorState={isError}
          usePagination
          useSorting
          showHeader={false}
          paginationProps={paginationProps}
          onRowClick={
            enableInvoiceBreakdown
              ? (row: InvoiceReport) => {
                  WrappedMixpanel.track('Invoice Clicked', {
                    companyId,
                    userId,
                    invoiceId: row.id,
                    memo: row.reference,
                  });
                  navigate(`${INVOICE_DETAILS_PATH(row.id)}`);
                }
              : undefined
          }
        />
      </Viewport>
      <Viewport devices={['phone', 'tablet']}>
        <InvoiceCardList
          total={invoiceReportResult?.count || 0}
          invoices={invoiceReportResult?.data ?? []}
          companyId={companyId}
          userId={userId}
        />
        {pageIndex > DEFAULT_PAGE_INDEX && (
          <Button
            icon="ChevronUp"
            onClick={() =>
              setPagination({
                pageIndex: pageIndex - 1,
                pageSize,
              })
            }
          />
        )}
        {pageIndex + 1 < totalNumberOfPages && (
          <Button
            icon="ChevronDown"
            onClick={() =>
              setPagination({
                pageIndex: pageIndex + 1,
                pageSize,
              })
            }
          />
        )}
      </Viewport>
    </>
  );
};
